import React from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Shield, Delete } from 'lucide-react';

export function PinLoginPanel({ pinInput, setPinInput, onLogin, error }) {
  const append = (digit) => {
    if (pinInput.length >= 6) return;
    setPinInput(pinInput + digit);
  };
  const backspace = () => setPinInput(pinInput.slice(0, -1));
  const clear = () => setPinInput('');

  const masked = pinInput.split('').map(() => '●').join(' ');

  return (
    <div className="border-2 border-[#111111] p-6 bg-white" data-testid="kiosk-pin-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
        <Shield className="w-4 h-4" /> Identifique-se com o seu PIN
      </h3>

      {/* PIN display */}
      <div
        className={`h-16 mb-4 flex items-center justify-center text-3xl font-mono tracking-[0.4em] border-2 ${
          error ? 'border-[#DC2626] bg-[#FEF2F2]' : 'border-[#E5E7EB] bg-[#F9FAFB]'
        }`}
        aria-label="PIN em uso"
        data-testid="kiosk-pin-display"
      >
        {pinInput.length > 0 ? masked : <span className="text-[#9CA3AF] text-sm uppercase tracking-widest">Digite o PIN</span>}
      </div>
      {error && (
        <p className="text-xs text-[#DC2626] uppercase tracking-widest text-center mb-3" data-testid="kiosk-pin-error">
          {error}
        </p>
      )}

      {/* Number pad */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <Button
            key={d}
            type="button"
            onClick={() => append(String(d))}
            className="h-16 text-2xl font-bold bg-white text-[#111111] border-2 border-[#E5E7EB] hover:bg-[#F3F4F6] hover:border-[#0029FF]"
            data-testid={`kiosk-key-${d}`}
          >
            {d}
          </Button>
        ))}
        <Button
          type="button"
          onClick={clear}
          className="h-16 text-xs font-bold uppercase tracking-widest bg-white text-[#DC2626] border-2 border-[#E5E7EB] hover:bg-[#FEF2F2] hover:border-[#DC2626]"
          data-testid="kiosk-key-clear"
        >
          Limpar
        </Button>
        <Button
          type="button"
          onClick={() => append('0')}
          className="h-16 text-2xl font-bold bg-white text-[#111111] border-2 border-[#E5E7EB] hover:bg-[#F3F4F6] hover:border-[#0029FF]"
          data-testid="kiosk-key-0"
        >
          0
        </Button>
        <Button
          type="button"
          onClick={backspace}
          className="h-16 bg-white text-[#111111] border-2 border-[#E5E7EB] hover:bg-[#F3F4F6] hover:border-[#0029FF]"
          data-testid="kiosk-key-backspace"
          aria-label="Apagar último dígito"
        >
          <Delete className="w-5 h-5" />
        </Button>
      </div>

      <Button
        onClick={onLogin}
        disabled={pinInput.length < 4}
        className="w-full h-14 bg-[#0029FF] hover:bg-[#0022CC] text-white font-bold text-base uppercase tracking-widest disabled:opacity-50"
        data-testid="kiosk-pin-login-btn"
      >
        <Camera className="w-5 h-5 mr-3" /> Entrar
      </Button>
    </div>
  );
}

export function ActionPanel({ currentRecord, actionButtons, onAction, onReset, geoStatus }) {
  const status = currentRecord?.status || 'not_started';
  const statusLabels = {
    working: 'Trabalhando', on_break: 'Em Pausa',
    completed: 'Dia Concluído', not_started: 'Aguardando Entrada'
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-2">
        <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-1">Status Atual</p>
        <p className="text-lg font-bold">{statusLabels[status] || 'Aguardando Entrada'}</p>
        {geoStatus === 'ok' && (
          <p className="text-xs text-[#059669] flex items-center gap-1">
            <span className="w-2 h-2 bg-[#059669] rounded-full inline-block" /> Localização verificada
          </p>
        )}
      </div>

      {actionButtons.map(btn => (
        <Button
          key={btn.action}
          data-testid={`kiosk-${btn.action}-btn`}
          onClick={() => onAction(btn.action)}
          disabled={!btn.enabled}
          className={`kiosk-btn ${btn.enabled ? btn.color : 'bg-[#E5E5E5]'} text-white`}
        >
          {btn.icon}<span className="ml-3">{btn.label}</span>
        </Button>
      ))}

      <Button variant="outline" onClick={onReset} className="mt-4" data-testid="kiosk-reset-btn">
        Trocar Funcionário
      </Button>
    </div>
  );
}
